import { type ReactNode, useMemo } from 'react'
import { Box } from '@mantine/core'

import type { InsightProps } from '@/components/Insight/Insight.types'
import { Text } from '@/components/Text/Text'

import { formatMoney } from '@/helpers/formatMoney.helper'

import classes from './Insight.module.css'

const PERCENT_FORMATTER = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
  style: 'percent',
})

const TOKEN_REGEX = /(<\/?strong>)|\{\{(value|percentage)\}\}/gi

function formatPercentage(rawPercentage?: number) {
  if (rawPercentage == null || !Number.isFinite(rawPercentage)) {
    return null
  }

  return PERCENT_FORMATTER.format(rawPercentage / 100)
}

function buildDescriptionParts({
  description,
  formattedValue,
  percentText,
}: {
  description: string
  formattedValue: string | null
  percentText: string | null
}): ReactNode[] {
  const nodes: ReactNode[] = []
  let isStrong = false
  let cursor = 0
  let buffer = ''
  let bufferIsStrong = isStrong
  let key = 0

  const nextKey = (prefix: string) => {
    const generatedKey = `${prefix}-${key}`

    key += 1

    return generatedKey
  }

  const flushBuffer = () => {
    if (!buffer) return

    if (bufferIsStrong) {
      nodes.push(<strong key={nextKey('strong')}>{buffer}</strong>)
    } else {
      nodes.push(buffer)
    }

    buffer = ''
  }

  const appendText = (text: string) => {
    if (!text) return

    if (buffer && bufferIsStrong !== isStrong) {
      flushBuffer()
    }

    if (!buffer) {
      bufferIsStrong = isStrong
    }

    buffer += text
  }

  const tokenIterator = description.matchAll(
    new RegExp(TOKEN_REGEX.source, TOKEN_REGEX.flags),
  )

  for (const match of tokenIterator) {
    const start = match.index ?? 0

    if (start > cursor) {
      appendText(description.slice(cursor, start))
    }

    cursor = start + match[0].length

    if (match[1]) {
      flushBuffer()
      isStrong = match[1].toLowerCase() === '<strong>'
    } else {
      const placeholder = match[2]

      flushBuffer()

      if (placeholder === 'value' && formattedValue != null) {
        if (isStrong) {
          appendText(formattedValue)
        } else {
          nodes.push(<strong key={nextKey('value')}>{formattedValue}</strong>)
        }
      } else if (placeholder === 'percentage' && percentText != null) {
        if (isStrong) {
          appendText(` (${percentText})`)
        } else {
          nodes.push(' (')
          nodes.push(<strong key={nextKey('percentage')}>{percentText}</strong>)
          nodes.push(')')
        }
      }
    }
  }

  if (cursor < description.length) {
    appendText(description.slice(cursor))
  }

  flushBuffer()

  return nodes
}

export function Insight({ body, metrics, title }: InsightProps) {
  const value = metrics?.value as number | undefined
  const percentage = metrics?.percentage as number | undefined

  const percentText = useMemo(() => formatPercentage(percentage), [percentage])
  const formattedValue = useMemo(
    () => (value == null ? null : formatMoney(value)),
    [value],
  )

  const parts = useMemo(
    () =>
      buildDescriptionParts({
        description: body,
        formattedValue,
        percentText,
      }),
    [body, formattedValue, percentText],
  )

  return (
    <Box className={classes.insight}>
      <Text mb={8} variant="body_1_bold">
        {title}
      </Text>
      <Text variant="body_2">{parts}</Text>
    </Box>
  )
}
